import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaEdit, FaHeart, FaRegHeart, FaReply, FaShareAlt, FaQuoteRight } from 'react-icons/fa';
import VerifiedBadge from './VerifiedBadge';
import RoleBadge from './RoleBadge';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface User {
    id: string;
    name: string;
    avatar?: string | null;
    role?: string;
    secondaryRole?: string;
    stats?: {
        posts: number;
        likes: number;
        joinDate: string;
    };
}

interface ForumPostProps {
    id: string;
    content: string;
    createdAt: string;
    updatedAt?: string;
    user: User;
    isMainPost?: boolean;
    likesCount?: number;
    isLiked?: boolean;
    currentUserId?: string;
    userRole?: string;
    onDelete?: (id: string) => void;
    onEdit?: (id: string, newContent: string) => Promise<void> | void;
    onReply?: (parentId: string, content: string) => Promise<void> | void;
    onLike?: (id: string) => void;
    onQuote?: (content: string, author: string) => void;
    image?: string | null; // For main post
    replies?: any[]; // For threaded replies
    editLink?: string; // For navigating to edit page
    onReplyClick?: () => void; // Custom reply handler (e.g. scroll to bottom)
    isReply?: boolean; // If true, render compact version
}

const ForumPost: React.FC<ForumPostProps> = ({
    id,
    content,
    createdAt,
    updatedAt,
    user,
    isMainPost = false,
    likesCount = 0,
    isLiked = false,
    currentUserId,
    userRole,
    onDelete,
    onEdit,
    onReply,
    onLike,
    onQuote,
    image,
    replies,
    editLink,
    onReplyClick,
    isReply = false
}) => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(content);
    const [isSaving, setIsSaving] = useState(false);
    const [isReplying, setIsReplying] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);

    // Safety check for missing ID
    if (!id) {
        return null;
    }

    // Fallback for missing user data
    const displayUser = (user && user.id) ? user : {
        id: 'unknown',
        name: 'Người dùng ẩn',
        avatar: null,
        role: 'user',
        secondaryRole: null,
        stats: { posts: 0, likes: 0, joinDate: '' }
    };

    // Safe access to user.id using displayUser
    const isOwner = currentUserId && displayUser.id !== 'unknown' ? currentUserId === displayUser.id : false;
    const canDelete = isOwner || userRole === 'admin';

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('vi-VN', {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleSave = async () => {
        if (!editContent.trim() || !onEdit) return;
        try {
            setIsSaving(true);
            await onEdit(id, editContent);
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to edit post', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmitReply = async () => {
        if (!replyContent.trim() || !onReply) return;

        try {
            setIsSubmittingReply(true);
            await onReply(id, replyContent);
            setReplyContent('');
            setIsReplying(false);
        } catch (error) {
            console.error('Failed to reply', error);
        } finally {
            setIsSubmittingReply(false);
        }
    };

    return (
        <div id={`post-${id}`} className={`bg-white dark:bg-gray-800 border ${isMainPost ? 'border-gray-300 dark:border-gray-600 shadow-md' : 'border-gray-200 dark:border-gray-700'} rounded-xl overflow-hidden mb-6`}>
            {/* Header Bar */}
            <div className={`px-4 py-2 border-b bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700 flex justify-between items-center text-xs ${isReply ? 'text-gray-400' : 'sm:text-sm text-gray-500'} dark:text-gray-400`}>
                <span>{formatDate(createdAt)}</span>
                <span className="font-mono">#{id.substring(0, 8)}</span>
            </div>

            <div className="flex flex-col sm:flex-row">
                {/* Left Column: User Info */}
                <div className={`${isReply ? 'sm:w-48 md:w-56 p-3' : 'sm:w-48 md:w-56 p-4'} shrink-0 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700 flex sm:flex-col items-center sm:items-center gap-3 sm:gap-4 bg-gray-50/30 dark:bg-gray-800`}>
                    {/* Avatar */}
                    <div className="relative group">
                        <div className={`${isReply ? 'w-10 h-10 sm:w-16 sm:h-16' : 'w-12 h-12 sm:w-24 sm:h-24'} rounded-full ring-4 ring-white dark:ring-gray-700 shadow-md overflow-hidden bg-gray-200`}>
                            {displayUser.avatar ? (
                                <img src={displayUser.avatar} alt={displayUser.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold text-xl sm:text-3xl">
                                    {displayUser.name.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className={`absolute bottom-0 right-0 w-3 h-3 sm:w-5 sm:h-5 rounded-full border-2 border-white dark:border-gray-800 bg-green-500`}></div>
                    </div>

                    {/* Name & Role */}
                    <div className="flex-1 sm:text-center text-left">
                        <h3 className={`font-bold text-gray-900 dark:text-white ${isReply ? 'text-sm sm:text-base' : 'text-base sm:text-lg'} leading-tight mb-1 flex items-center justify-start sm:justify-center gap-1.5 text-left sm:text-center`}>
                            {displayUser.name}
                            <VerifiedBadge role={displayUser.role} className="w-5 h-5" />
                        </h3>
                        <div className="flex flex-wrap sm:justify-center gap-1.5 mt-1 sm:mt-2">
                            {displayUser.role && <RoleBadge role={displayUser.role} size="sm" />}
                            {displayUser.secondaryRole && <RoleBadge role={displayUser.secondaryRole} size="sm" />}
                        </div>
                    </div>

                    {/* Stats (Hidden on mobile, only show for main post) */}
                    {isMainPost && (
                        <div className="hidden sm:block w-full text-xs text-gray-500 dark:text-gray-400 space-y-1 mt-2 pt-3 border-t border-gray-200 dark:border-gray-700/50">
                            <div className="flex justify-between">
                                <span>Tham gia:</span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                    {displayUser.stats?.joinDate ? new Date(displayUser.stats.joinDate).toLocaleDateString('vi-VN') : '--'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>Bài viết:</span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                    {displayUser.stats?.posts ?? 0}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>Đã nhận:</span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                    {displayUser.stats?.likes ?? 0} thích
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column: Content */}
                <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-gray-800">
                    <div className="p-4 sm:p-6 flex-1">
                        {isEditing ? (
                            <div className="space-y-4">
                                <textarea
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    className="w-full p-4 bg-white dark:bg-gray-900 border border-blue-300 dark:border-blue-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none min-h-[200px] text-gray-800 dark:text-gray-200"
                                    placeholder="Nội dung bài viết..."
                                />
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => { setIsEditing(false); setEditContent(content); }}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg dark:text-gray-300 dark:hover:bg-gray-700"
                                    >
                                        Hủy
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-sm"
                                    >
                                        {isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {isMainPost && image && (
                                    <div className="mb-4 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-700">
                                        <img src={image} alt="Main content" className="max-w-full h-auto max-h-[500px] object-contain mx-auto" />
                                    </div>
                                )}

                                <div className="prose prose-sm sm:prose-base dark:prose-invert max-w-none text-gray-800 dark:text-gray-200 break-words leading-relaxed">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        rehypePlugins={[rehypeRaw]}
                                    >
                                        {content}
                                    </ReactMarkdown>
                                </div>

                                {updatedAt && updatedAt !== createdAt && (
                                    <div className="text-xs text-gray-400 italic mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
                                        Đã chỉnh sửa lần cuối: {formatDate(updatedAt)}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Action Bar */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                        {/* Reactions */}
                        <div className="flex items-center gap-2">
                            {onLike && (
                                <button
                                    onClick={() => onLike(id)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isLiked
                                        ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                                        : 'text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    {isLiked ? <FaHeart /> : <FaRegHeart />}
                                    {likesCount > 0 && <span>{likesCount}</span>}
                                    <span className="hidden sm:inline">Thích</span>
                                </button>
                            )}
                        </div>

                        {/* Tools */}
                        <div className="flex items-center gap-2">
                            {!isOwner && onQuote && (
                                <button
                                    onClick={() => onQuote(content, displayUser.name)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors"
                                    title="Trích dẫn"
                                >
                                    <FaQuoteRight size={14} />
                                    <span className="hidden sm:inline">Trích dẫn</span>
                                </button>
                            )}


                            {(onReply || onReplyClick) && (
                                <button
                                    onClick={() => onReplyClick ? onReplyClick() : setIsReplying(!isReplying)}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${isReplying
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30'
                                        }`}
                                >
                                    <FaReply size={14} />
                                    <span>{isReplying ? 'Hủy' : 'Phản hồi'}</span>
                                </button>
                            )}

                            {(isOwner || canDelete) && (
                                <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                            )}

                            {isOwner && (onEdit || editLink) && (
                                <button
                                    onClick={() => editLink ? navigate(editLink) : setIsEditing(true)}
                                    className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                                    title="Chỉnh sửa"
                                >
                                    <FaEdit />
                                </button>
                            )}

                            {canDelete && onDelete && (
                                <button
                                    onClick={() => onDelete(id)}
                                    className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                                    title="Xóa"
                                >
                                    <FaTrash />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div >

            {/* Inline Reply Box */}
            {
                isReplying && (
                    <div className="mx-4 sm:mx-0 mb-4 sm:ml-[200px] bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 animate-fadeIn relative z-10">
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <textarea
                                    value={replyContent}
                                    onChange={(e) => setReplyContent(e.target.value)}
                                    placeholder={`Phản hồi ${displayUser.name}...`}
                                    className="w-full p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none min-h-[80px] text-sm text-gray-900 dark:text-white"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSubmitReply();
                                        }
                                    }}
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                    <button
                                        onClick={() => setIsReplying(false)}
                                        className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                    >
                                        Hủy
                                    </button>
                                    <button
                                        onClick={handleSubmitReply}
                                        disabled={isSubmittingReply || !replyContent.trim()}
                                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all disabled:opacity-50"
                                    >
                                        {isSubmittingReply ? 'Đang gửi...' : 'Gửi phản hồi'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Threaded Replies */}
            {
                replies && replies.length > 0 && (
                    <div className="ml-4 sm:ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-700 mt-4 space-y-4">
                        {replies.filter(r => r && r.id && r.user).map((reply) => (
                            <ForumPost
                                key={reply.id}
                                {...reply}
                                user={reply.user}
                                currentUserId={currentUserId}
                                userRole={userRole}
                                onDelete={onDelete}
                                onEdit={onEdit}
                                onReply={onReply}
                                onLike={onLike}
                                onQuote={onQuote}
                                isReply={true}
                            />
                        ))}
                    </div>
                )
            }
        </div >
    );
};

export default ForumPost;
